<template>
  <div class="listar-sala-planta">
    <h2 class="text-center mb-4">Visualización de Plantas por Sala</h2>
    
    <!-- Filtros generales -->
    <div class="filters my-3 d-flex flex-wrap gap-2 align-items-center justify-content-center">
      <input
        type="text"
        v-model="filterSala"
        class="form-control form-control-sm"
        placeholder="Filtrar por sala..."
        style="max-width: 200px;"
        aria-label="Filtrar por sala"
      />
      <input
        type="text"
        v-model="filterPlanta"
        class="form-control form-control-sm"
        placeholder="Filtrar por planta..."
        style="max-width: 200px;"
        aria-label="Filtrar por planta"
      />
      <button class="btn btn-secondary btn-sm" @click="clearFilters">Limpiar Filtros</button>
    </div>

    <!-- Listado agrupado por sala (acordeón personalizado) con paginación -->
    <div v-if="groupedSalas.length > 0">
      <div class="accordion" id="salasAccordion">
        <div 
          v-for="salaGroup in paginatedSalaGroups" 
          :key="salaGroup.sala.id" 
          class="accordion-item mb-2"
        >
          <h2 class="accordion-header" :id="'heading-' + salaGroup.sala.id">
            <button 
              class="accordion-button" 
              type="button" 
              @click="toggleAccordion(salaGroup.sala.id)"
            >
              {{ salaGroup.sala.nombre }}
              <span class="ms-2 badge bg-info">
                {{ salaGroup.totalPlantas }} plantas activas
              </span>
            </button>
          </h2>
          <div 
            v-show="isAccordionOpen(salaGroup.sala.id)" 
            class="accordion-body"
          >
            <!-- Resumen de la sala -->
            <div class="sala-summary mb-2">
              <p><strong>Tipo:</strong> {{ salaGroup.sala.tipo }}</p>
              <!-- Puedes agregar más datos relevantes aquí -->
            </div>
            <!-- Agrupado por lote -->
            <div v-for="loteGroup in salaGroup.groupByLote" :key="loteGroup.lote.id" class="mb-3">
              <h6>Lote: {{ loteGroup.lote.nombre }} ({{ loteGroup.plantas.length }} plantas)</h6>
              <!-- Lista de plantas -->
              <div class="plantas-list mt-2">
                <div 
                  v-for="planta in loteGroup.plantas" 
                  :key="planta.id" 
                  class="plant-item card p-2 mb-1"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ planta.codigo }}</strong>
                      <br>
                      <small>ID: {{ planta.id }}</small>
                    </div>
                    <div>
                      <small class="text-muted">
                        Asignación: {{ formatDate(planta.fecha_asignacion) }}
                      </small>
                      <br>
                      <small class="text-muted">
                        Salida: {{ formatDate(planta.fecha_salida) || 'N/A' }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div> <!-- Fin de grupo por lote -->
          </div>
        </div>
      </div>
      <!-- Paginación de salas -->
      <nav v-if="totalSalaPages > 1" class="mt-3">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item" :class="{ disabled: currentSalaPage === 1 }">
            <a class="page-link" href="#" @click.prevent="currentSalaPage--">Anterior</a>
          </li>
          <li 
            v-for="page in totalSalaPages" 
            :key="page" 
            class="page-item" 
            :class="{ active: currentSalaPage === page }"
          >
            <a class="page-link" href="#" @click.prevent="currentSalaPage = page">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentSalaPage === totalSalaPages }">
            <a class="page-link" href="#" @click.prevent="currentSalaPage++">Siguiente</a>
          </li>
        </ul>
      </nav>
    </div>
    <div v-else class="text-center text-muted">
      No se encontraron relaciones.
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import { mapGetters } from 'vuex';

export default {
  name: 'ListarSalaPlanta',

  props: {
    proyecto_id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      salaPlantas: [], // Relaciones obtenidas de la API
      filterSala: '',
      filterPlanta: '',
      selectedLoteFilter: '',
      filterLote: '',
      sortLote: '',
      currentSalaPage: 1,
      salaPageSize: 3, // Número de salas por página
      openAccordionId: null, // ID del acordeón abierto
    };
  },
  computed: {
    ...mapGetters(['getClubActual']),
    // Agrupar relaciones por sala, y dentro por lote
    groupedSalas() {
      const groups = {};
      this.salaPlantas.forEach(item => {
        if (!groups[item.sala.id]) {
          groups[item.sala.id] = {
            sala: item.sala,
            relaciones: [],
          };
        }
        // Solo incluir relaciones sin fecha_salida
        if (!item.fecha_salida) {
          groups[item.sala.id].relaciones.push(item);
        }
      });
      Object.keys(groups).forEach(salaId => {
        const loteGroups = {};
        groups[salaId].relaciones.forEach(rel => {
          const loteId = rel.planta.lote?.id;
          if (loteId) {
            if (!loteGroups[loteId]) {
              loteGroups[loteId] = {
                lote: rel.planta.lote,
                plantas: [],
              };
            }
            loteGroups[loteId].plantas.push(rel.planta);
          }
        });
        groups[salaId].groupByLote = Object.values(loteGroups);
        groups[salaId].totalPlantas = groups[salaId].relaciones.length;
      });
      let result = Object.values(groups);
      if (this.filterSala) {
        result = result.filter(group => group.sala.nombre.toLowerCase().includes(this.filterSala.toLowerCase()));
      }
      return result;
    },
    paginatedSalaGroups() {
      const start = (this.currentSalaPage - 1) * this.salaPageSize;
      return this.groupedSalas.slice(start, start + this.salaPageSize);
    },
    totalSalaPages() {
      return Math.ceil(this.groupedSalas.length / this.salaPageSize);
    },
  },
  mounted() {
    this.fetchSalaPlantas();
  },
  methods: {
    async fetchSalaPlantas() {
      try {
        const club_id = this.getClubActual.id;
        const proyecto_id = this.proyecto_id;
        const res = await axios.get('/api/sala_plantas', {
          params: { club_id, proyecto_id },
        });
        // Se espera que la API devuelva relaciones con { sala, planta, fecha_asignacion, fecha_salida, posicion, notas }
        this.salaPlantas = res.data;
      } catch (error) {
        console.error('Error al obtener relaciones sala-planta:', error);
      }
    },
    clearFilters() {
      this.filterSala = '';
      this.filterPlanta = '';
      this.selectedLoteFilter = '';
      this.filterLote = '';
      this.sortLote = '';
      this.currentSalaPage = 1;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    },
    toggleAccordion(id) {
      this.openAccordionId = this.openAccordionId === id ? null : id;
    },
    isAccordionOpen(id) {
      return this.openAccordionId === id;
    },
  },
};
</script>

<style scoped>
.listar-sala-planta {
  padding: 20px;
  background-color: #f7f8fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.filters input,
.filters select {
  width: 100%;
  max-width: 200px;
  margin-bottom: 10px;
}
.accordion-button {
  font-size: 1rem;
  padding: 10px;
  transition: background-color 0.3s ease;
  cursor: pointer;
  background-color: #e9ecef;
  border: none;
  width: 100%;
  text-align: left;
  outline: none;
}
.accordion-button:hover {
  background-color: #e2e6ea;
}
.accordion-body {
  font-size: 0.9rem;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #ced4da;
  margin-top: 5px;
}
.sala-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
}
.sala-item.selected {
  background-color: #d4edda;
}
.sala-icon-container {
  width: 60px;
  height: 60px;
}
.sala-icon {
  width: 60px;
  height: 60px;
  background-color: #dee2e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.plant-item {
  cursor: default;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  transition: box-shadow 0.2s ease;
}
.plant-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.plantas-list .card {
  margin-bottom: 8px;
}
.sala-summary {
  font-size: 0.85rem;
  border: 1px solid #ced4da;
  padding: 10px;
  border-radius: 4px;
  background-color: #e9ecef;
  margin-top: 10px;
}
.card-footer {
  padding: 8px;
}
.pagination {
  margin-top: 15px;
}
@media (max-width: 576px) {
  .btn {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
}
</style>
